/**
 * Dezimalstring ⇄ Mikro-Einheiten — **ohne Float** (CLAUDE.md §6).
 *
 * Altdaten-Quellen (CSV/JSON aus Fremdsystemen) liefern Geld als Dezimalstring
 * („8.81", „729.3", „3000"). Der naheliegende Weg `parseFloat(x) * 1_000_000`
 * ist ein Buchungsfehler mit Ansage: `8.81` ist binär nicht darstellbar,
 * `parseFloat('8.81') * 1_000_000` ergibt `8809999.999999999`. Deshalb wird hier
 * ausschließlich auf Strings und `bigint` gerechnet:
 * Vor- und Nachkommateil werden getrennt, der Nachkommateil auf sechs Stellen
 * aufgefüllt und beides als Ganzzahl zusammengesetzt.
 */
import { MICRO_UNITS_PER_MAJOR } from '../money';

/** Nachkommastellen einer Mikro-Einheit (1 EUR = 1.000.000 Mikro-EUR). */
export const MICRO_FRACTION_DIGITS = 6;

/**
 * Zulässiger Dezimalstring: optionales Minus, mindestens eine Vorkommastelle,
 * optional bis zu sechs Nachkommastellen. Bewusst streng — kein Tausender-
 * trenner, kein Komma, keine Exponentialschreibweise, kein „1." oder „.5".
 * Alles, was hier durchfällt, ist ein Datenfehler und wird nicht geraten (§15).
 */
const DECIMAL_AMOUNT_PATTERN = /^-?\d+(?:\.\d{1,6})?$/;

/** Ist `raw` ein Dezimalstring, den `decimalToMicroUnits` verlustfrei umrechnet? */
export function isDecimalAmount(raw: string): boolean {
  return DECIMAL_AMOUNT_PATTERN.test(raw.trim());
}

/**
 * Dezimalstring → Mikro-Einheiten, exakt und float-frei.
 * `"8.81"` → `8_810_000n`, `"729.3"` → `729_300_000n`, `"3000"` → `3_000_000_000n`.
 *
 * @throws RangeError bei allem, was `isDecimalAmount` ablehnt (inkl. mehr als
 *   sechs Nachkommastellen — das wäre stilles Runden von Geld).
 */
export function decimalToMicroUnits(raw: string): bigint {
  const value = raw.trim();
  if (!DECIMAL_AMOUNT_PATTERN.test(value)) {
    throw new RangeError(`"${raw}" ist kein gültiger Dezimalbetrag (erwartet z. B. "1832.95").`);
  }
  const isNegative = value.startsWith('-');
  const unsigned = isNegative ? value.slice(1) : value;
  const dot = unsigned.indexOf('.');
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fraction = dot === -1 ? '' : unsigned.slice(dot + 1);
  const micro =
    BigInt(whole) * MICRO_UNITS_PER_MAJOR + BigInt(fraction.padEnd(MICRO_FRACTION_DIGITS, '0'));
  return isNegative ? -micro : micro;
}

/**
 * Mikro-Einheiten → deutscher Euro-Text für Meldungen („1.832,95 €", „-0,01 €").
 * Reine `bigint`-Arithmetik; auf Cent kaufmännisch gerundet, weil es hier um
 * **Anzeige** geht — gerechnet wird nirgends mit dem Ergebnis.
 */
export function formatMicroAsEuroDe(micro: bigint): string {
  const isNegative = micro < 0n;
  const absolute = isNegative ? -micro : micro;
  const cents = (absolute + 5_000n) / 10_000n; // 1 Cent = 10.000 Mikro
  const whole = (cents / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const rest = (cents % 100n).toString().padStart(2, '0');
  return `${isNegative ? '-' : ''}${whole},${rest} €`;
}
